import React, { useEffect, useMemo, useRef, useState } from "react";
import { getCurrentUser } from "../utils/auth";
import UserAccessSummary from "../components/layout/UserAccessSummary";
import { getUserAccessInfo } from "../utils/accessFilters";
import useAccessOverrideVersion from "../hooks/useAccessOverrideVersion";
import { MRP_PO_BASE_FILTER_TOKEN } from "../utils/mrpDrilldown";

const API_BASE = process.env.REACT_APP_API_URL || "https://api.portpilot.co";
const ITEM_ALL_KEY = "ALL";
const ITEM_ALL_IN_ONE_KEY = "ALL_IN_ONE";
const WAREHOUSE_ALL_KEY = "ALL";

const SERIES_ROWS = [
  { key: "po_lines_created", label: "A) PO Lines (Created Week)", kind: "count", group: "PO Group" },
  { key: "po_qty_created", label: "B) PO Qty (Created Week)", kind: "qty", group: "PO Group" },
  { key: "po_lines_mabd", label: "C) PO Lines (MABD Week)", kind: "count", group: "PO Group" },
  { key: "po_qty_mabd", label: "D) PO Qty (MABD Week)", kind: "qty", group: "PO Group" },
  { key: "shipped_qty", label: "E) Shipped Qty (Shipped Week)", kind: "qty", group: "PO Group" },
  { key: "occupied_inv_wk_end", label: "F) Occupied Inv - WK End", kind: "qty", group: "Warehouse / Inventory Group" },
  { key: "avail_inv_wk_end", label: "G) Avail Inv - WK End", kind: "qty", group: "Warehouse / Inventory Group" },
  {
    key: "oh_inv_wk_end",
    label: "H) OH Inv - WK End",
    kind: "qty",
    tooltip: "excl. QC, Returns",
    group: "Warehouse / Inventory Group",
  },
  { key: "intransit_depart_pieces", label: "I) In Transit - Depart", kind: "qty", group: "In-transit Group" },
  { key: "intransit_arrival_pieces", label: "J) In Transit - Arrival", kind: "qty", group: "In-transit Group" },
  { key: "forecast_data_dump_qty", label: "K) Forecast - Data Dump", kind: "qty", group: "Forecast Group" },
  { key: "forecast_event_qty", label: "L) Forecast - Event Code", kind: "qty", group: "Forecast Group" },
  { key: "supplier_flow_wm_delivery_qty", label: "M) Supplier Flow - WM Delivery", kind: "qty", group: "Forecast Group" },
  {
    key: "projected_inventory_wk_end",
    label: "P) Projected Inventory - WK End",
    kind: "qty",
    highlightNegative: true,
    group: "Calculation",
  },
];
const ROW_GROUP_ORDER = [
  "PO Group",
  "Warehouse / Inventory Group",
  "In-transit Group",
  "Forecast Group",
  "Calculation",
];
const ROW_PRESET_OPTIONS = [
  { key: "DEFAULT", label: "Default" },
  { key: "PLANNING_FOCUS", label: "Planning Focus" },
  { key: "ALL_ROWS", label: "All Rows" },
];
const ROW_PRESET_ROW_KEYS = {
  DEFAULT: [
    "po_qty_created",
    "shipped_qty",
    "avail_inv_wk_end",
    "intransit_arrival_pieces",
    "forecast_data_dump_qty",
    "forecast_event_qty",
    "projected_inventory_wk_end",
  ],
  PLANNING_FOCUS: [
    "avail_inv_wk_end",
    "intransit_arrival_pieces",
    "forecast_data_dump_qty",
    "forecast_event_qty",
    "projected_inventory_wk_end",
  ],
  ALL_ROWS: SERIES_ROWS.map((row) => row.key),
};
const ROW_GROUPS = ROW_GROUP_ORDER.map((group) => ({
  group,
  rows: SERIES_ROWS.filter((row) => row.group === group),
})).filter((group) => group.rows.length > 0);
const SERIES_DRILLDOWN_CONFIG = {
  po_lines_created: { path: "/pos", dateField: "create_date", poBaseFilterToken: MRP_PO_BASE_FILTER_TOKEN },
  po_qty_created: { path: "/pos", dateField: "create_date", poBaseFilterToken: MRP_PO_BASE_FILTER_TOKEN },
  po_lines_mabd: { path: "/pos", dateField: "mabd", poBaseFilterToken: MRP_PO_BASE_FILTER_TOKEN },
  po_qty_mabd: { path: "/pos", dateField: "mabd", poBaseFilterToken: MRP_PO_BASE_FILTER_TOKEN },
  shipped_qty: { path: "/routing/routing-status", dateField: "shipped_date" },
  intransit_depart_pieces: {
    path: "/my-containers",
    dateField: "etd",
    drill: "mrp_intransit_depart",
  },
  intransit_arrival_pieces: {
    path: "/my-containers",
    dateField: "arrival_effective_date",
    drill: "mrp_intransit_arrival",
  },
};

function toDateInputValue(date) {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function defaultDateWindow() {
  const today = new Date();
  const currentWeekStart = new Date(today);
  const day = currentWeekStart.getDay(); // Sun=0 ... Sat=6
  const delta = (day - 6 + 7) % 7;
  currentWeekStart.setHours(0, 0, 0, 0);
  currentWeekStart.setDate(currentWeekStart.getDate() - delta);
  const currentWeekEnd = new Date(currentWeekStart);
  currentWeekEnd.setDate(currentWeekEnd.getDate() + 6);
  const start = new Date(currentWeekStart);
  const end = new Date(currentWeekEnd);
  start.setDate(start.getDate() - 12 * 7);
  end.setDate(end.getDate() + 51 * 7);
  return {
    start: toDateInputValue(start),
    end: toDateInputValue(end),
    currentWeekStart: toDateInputValue(currentWeekStart),
  };
}

function formatValue(value, kind) {
  if (value === null || value === undefined || value === "") return "";
  const number = Number(value);
  if (!Number.isFinite(number)) return "";
  if (kind === "count") return Math.round(number).toLocaleString();
  return number.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function filenameFromDisposition(value = "") {
  const match = /filename\*?=(?:UTF-8'')?["']?([^"';]+)/i.exec(value);
  if (match && match[1]) {
    try {
      return decodeURIComponent(match[1]);
    } catch (err) {
      return match[1];
    }
  }
  return "";
}

function formatTimestampForFilename(date) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}-${pad(date.getHours())}${pad(
    date.getMinutes()
  )}${pad(date.getSeconds())}`;
}

function normalizeOptions(rawOptions) {
  if (!Array.isArray(rawOptions)) return [];
  return rawOptions
    .map((option) => {
      const sku = `${option?.sku || ""}`.trim();
      const label = `${option?.label || sku}`.trim();
      const productCode = `${option?.product_code || ""}`.trim();
      if (!sku) return null;
      return { sku, label: label || sku, productCode };
    })
    .filter(Boolean);
}

function normalizeAccessTokens(tokens) {
  if (!Array.isArray(tokens)) return [];
  const seen = new Set();
  const output = [];
  for (const raw of tokens) {
    const token = String(raw || "").trim().toUpperCase();
    if (!token || token === "ALL" || token === "__NULL__" || seen.has(token)) continue;
    seen.add(token);
    output.push(token);
  }
  return output;
}

function formatWhseScopeLabel(tokens) {
  const normalized = normalizeAccessTokens(tokens);
  if (!normalized.length) return "ALL";
  if (normalized.length <= 3) return normalized.join(", ");
  return `${normalized.slice(0, 3).join(", ")} +${normalized.length - 3}`;
}

function toIsoDate(dateValue) {
  const year = dateValue.getFullYear();
  const month = `${dateValue.getMonth() + 1}`.padStart(2, "0");
  const day = `${dateValue.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function currentWeekStartIso() {
  const now = new Date();
  const day = now.getDay(); // Sun=0 ... Sat=6
  const delta = (day - 6 + 7) % 7;
  const start = new Date(now);
  start.setDate(start.getDate() - delta);
  return toIsoDate(start);
}

function normalizeSelectedItems(items) {
  if (!Array.isArray(items)) return [];
  const seen = new Set();
  const output = [];
  for (const raw of items) {
    const token = `${raw || ""}`.trim();
    if (!token || seen.has(token)) continue;
    seen.add(token);
    output.push(token);
  }
  return output;
}

function toNumber(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
}

function formatRecommendationWeek(week) {
  if (!week || typeof week !== "object") return "";
  const weekNo = String(week.week_of_year || "").padStart(2, "0");
  const weekLabel = weekNo ? `W${weekNo}` : "";
  const spanLabel = `${week.span_label || ""}`.trim();
  if (weekLabel && spanLabel) return `${weekLabel} (${spanLabel})`;
  return weekLabel || spanLabel || "";
}

function recommendationStatusConfig(summary) {
  const status = `${summary?.status || ""}`.trim().toLowerCase();
  if (status === "shortage") {
    return {
      label: "Shortage Detected",
      panelClass: "border-amber-200 bg-amber-50",
      badgeClass: "border-amber-300 bg-white text-amber-800",
    };
  }
  if (status === "no_shortage") {
    return {
      label: "No Shortage In Coverage",
      panelClass: "border-emerald-200 bg-emerald-50",
      badgeClass: "border-emerald-300 bg-white text-emerald-800",
    };
  }
  return {
    label: "Recommendation Unavailable",
    panelClass: "border-slate-200 bg-slate-50",
    badgeClass: "border-slate-300 bg-white text-slate-700",
  };
}

function recommendationFieldValue(summary, field) {
  if (!summary || typeof summary !== "object") return "Not available";
  if (field === "first_shortage_week") {
    const formatted = formatRecommendationWeek(summary.first_shortage_week);
    if (formatted) return formatted;
    return summary.status === "no_shortage" ? "None" : "Not available";
  }
  if (field === "required_factory_ship_week") {
    const formatted = formatRecommendationWeek(summary.required_factory_ship_week);
    if (formatted) return formatted;
    return summary.status === "no_shortage" ? "None" : "Not available";
  }
  if (field === "required_qty") {
    if (summary.required_qty !== null && summary.required_qty !== undefined && summary.required_qty !== "") {
      return formatValue(summary.required_qty, "qty");
    }
    return summary.status === "no_shortage" ? "None" : "Not available";
  }
  if (field === "suggested_container_plan") {
    const value = `${summary.suggested_container_plan || ""}`.trim();
    return value || "Not available";
  }
  return "Not available";
}

function orderedRowKeys(rowKeys) {
  const orderMap = new Map(SERIES_ROWS.map((row, index) => [row.key, index]));
  return Array.from(new Set(rowKeys || []))
    .filter((rowKey) => orderMap.has(rowKey))
    .sort((left, right) => orderMap.get(left) - orderMap.get(right));
}

function sameRowKeySet(left, right) {
  const orderedLeft = orderedRowKeys(left);
  const orderedRight = orderedRowKeys(right);
  if (orderedLeft.length !== orderedRight.length) return false;
  return orderedLeft.every((rowKey, index) => rowKey === orderedRight[index]);
}

export default function MRP() {
  const me = getCurrentUser();
  const accessVersion = useAccessOverrideVersion();
  const accessInfo = useMemo(() => {
    void accessVersion;
    return getUserAccessInfo();
  }, [accessVersion]);
  const defaults = useMemo(() => defaultDateWindow(), []);
  const currentWeekStart = defaults.currentWeekStart || currentWeekStartIso();

  const [startDate, setStartDate] = useState(defaults.start);
  const [endDate, setEndDate] = useState(defaults.end);
  const [selectedItems, setSelectedItems] = useState([ITEM_ALL_IN_ONE_KEY]);
  const [itemOptions, setItemOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [rowPreset, setRowPreset] = useState("DEFAULT");
  const [rowFilterOpen, setRowFilterOpen] = useState(false);
  const [visibleRowKeys, setVisibleRowKeys] = useState(() => orderedRowKeys(ROW_PRESET_ROW_KEYS.DEFAULT));

  const [weeks, setWeeks] = useState([]);
  const [tables, setTables] = useState([]);
  const [compiledWarehouse, setCompiledWarehouse] = useState({
    whseCodes: [],
    label: "ALL",
  });
  const [inventorySnapshotNotice, setInventorySnapshotNotice] = useState(null);
  const [compiled, setCompiled] = useState(false);
  const [compiling, setCompiling] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");
  const [lastCompiledAt, setLastCompiledAt] = useState("");
  const [lastCompiledPayload, setLastCompiledPayload] = useState(null);
  const tableScrollRefs = useRef({});
  const currentWeekHeaderRefs = useRef({});

  const allSkuKeys = useMemo(() => itemOptions.map((option) => option.sku), [itemOptions]);
  const activeCustTokens = useMemo(
    () => (Array.isArray(accessInfo?.activeCustTokens) ? accessInfo.activeCustTokens : []),
    [accessInfo?.activeCustTokens]
  );
  const activeWhsTokens = useMemo(
    () => (Array.isArray(accessInfo?.activeWhsTokens) ? accessInfo.activeWhsTokens : []),
    [accessInfo?.activeWhsTokens]
  );
  const selectedSkuKeys = useMemo(
    () => selectedItems.filter((token) => token !== ITEM_ALL_KEY && token !== ITEM_ALL_IN_ONE_KEY),
    [selectedItems]
  );
  const visibleRowKeySet = useMemo(() => new Set(visibleRowKeys), [visibleRowKeys]);
  const visibleSeriesRows = useMemo(
    () => SERIES_ROWS.filter((row) => visibleRowKeySet.has(row.key)),
    [visibleRowKeySet]
  );
  const presetAligned = useMemo(
    () => sameRowKeySet(visibleRowKeys, ROW_PRESET_ROW_KEYS[rowPreset] || []),
    [rowPreset, visibleRowKeys]
  );

  const applyAllFlagRule = (candidate) => {
    const next = new Set(candidate);
    const hasAllSku =
      allSkuKeys.length > 0 &&
      allSkuKeys.every((sku) => next.has(sku));
    const hasAllInOne = next.has(ITEM_ALL_IN_ONE_KEY);
    if (hasAllSku && hasAllInOne) next.add(ITEM_ALL_KEY);
    else next.delete(ITEM_ALL_KEY);
    return normalizeSelectedItems(Array.from(next));
  };

  const toggleItem = (token) => {
    if (token === ITEM_ALL_KEY) {
      const hasAll = selectedItems.includes(ITEM_ALL_KEY);
      if (hasAll) {
        setSelectedItems([]);
      } else {
        setSelectedItems(normalizeSelectedItems([ITEM_ALL_KEY, ITEM_ALL_IN_ONE_KEY, ...allSkuKeys]));
      }
      return;
    }

    const next = new Set(selectedItems);
    if (next.has(token)) next.delete(token);
    else next.add(token);
    setSelectedItems(applyAllFlagRule(next));
  };

  useEffect(() => {
    let cancelled = false;
    const loadItemOptions = async () => {
      setOptionsLoading(true);
      setError("");
      try {
        const headers = {};
        if (me?.email) headers["X-User-Email"] = me.email;
        if (me?.role) headers["X-User-Role"] = me.role || "";
        const url = new URL(`${API_BASE}/api/mrp/operational-planner/item-options`);
        if (activeCustTokens.length) {
          url.searchParams.set("cust_codes", activeCustTokens.join(","));
        }
        const resp = await fetch(url.toString(), { headers });
        const body = await resp.json().catch(() => ({}));
        if (!resp.ok || body?.ok !== true) {
          throw new Error(body?.error || "Unable to load Item No options.");
        }
        if (cancelled) return;
        const options = normalizeOptions(body?.options);
        setItemOptions(options);
        setSelectedItems((prev) => {
          const valid = new Set([ITEM_ALL_KEY, ITEM_ALL_IN_ONE_KEY, ...options.map((option) => option.sku)]);
          const filtered = prev.filter((token) => valid.has(token));
          if (!filtered.length) {
            return [ITEM_ALL_IN_ONE_KEY];
          }
          return applyAllFlagRule(filtered);
        });
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Unable to load Item No options.");
        }
      } finally {
        if (!cancelled) setOptionsLoading(false);
      }
    };
    loadItemOptions();
    return () => {
      cancelled = true;
    };
  }, [me?.email, me?.role, activeCustTokens]); // eslint-disable-line react-hooks/exhaustive-deps

  const dropdownSummary = useMemo(() => {
    if (selectedItems.includes(ITEM_ALL_KEY)) return "ALL";
    if (selectedItems.length === 1 && selectedItems.includes(ITEM_ALL_IN_ONE_KEY)) return "All-in-One";
    if (!selectedItems.length) return "Select Item";
    if (selectedItems.length === 1) {
      const token = selectedItems[0];
      const matched = itemOptions.find((option) => option.sku === token);
      return matched?.label || token;
    }
    return `${selectedItems.length} selected`;
  }, [itemOptions, selectedItems]);

  const canCompile =
    !optionsLoading &&
    !compiling &&
    !!startDate &&
    !!endDate &&
    selectedItems.length > 0;

  const applyRowPreset = (presetKey) => {
    const nextPreset = ROW_PRESET_ROW_KEYS[presetKey] ? presetKey : "DEFAULT";
    setRowPreset(nextPreset);
    setVisibleRowKeys(orderedRowKeys(ROW_PRESET_ROW_KEYS[nextPreset] || ROW_PRESET_ROW_KEYS.DEFAULT));
  };

  const toggleRowVisibility = (rowKey) => {
    setVisibleRowKeys((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return orderedRowKeys(Array.from(next));
    });
  };

  const handleCompile = async () => {
    setDropdownOpen(false);
    setRowFilterOpen(false);
    if (!canCompile) return;
    setCompiling(true);
    setError("");
    try {
      const headers = { "Content-Type": "application/json" };
      if (me?.email) headers["X-User-Email"] = me.email;
      if (me?.role) headers["X-User-Role"] = me.role || "";

      const payload = {
        start_date: startDate,
        end_date: endDate,
        selected_items: selectedItems,
        cust_codes: activeCustTokens,
        whse_codes: activeWhsTokens,
      };

      const resp = await fetch(`${API_BASE}/api/mrp/operational-planner/compile`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok || body?.ok !== true) {
        throw new Error(body?.error || "Unable to compile planner.");
      }

      const nextWeeks = Array.isArray(body?.weeks) ? body.weeks : [];
      const nextTables = Array.isArray(body?.tables) ? body.tables : [];
      setWeeks(nextWeeks);
      setTables(nextTables);
      if (body?.range?.start_date) setStartDate(body.range.start_date);
      if (body?.range?.end_date) setEndDate(body.range.end_date);
      if (Array.isArray(body?.selected_items)) {
        setSelectedItems(normalizeSelectedItems(body.selected_items));
      }
      const nextCompiledWhseCodes = normalizeAccessTokens(body?.warehouse?.whse_codes || activeWhsTokens);
      setCompiledWarehouse({
        whseCodes: nextCompiledWhseCodes,
        label: formatWhseScopeLabel(nextCompiledWhseCodes),
      });
      setInventorySnapshotNotice(
        body?.inventory_snapshot && typeof body.inventory_snapshot === "object"
          ? body.inventory_snapshot
          : null
      );
      setCompiled(true);
      setLastCompiledAt(new Date().toLocaleString());
      setLastCompiledPayload({
        start_date: body?.range?.start_date || payload.start_date,
        end_date: body?.range?.end_date || payload.end_date,
        selected_items: Array.isArray(body?.selected_items) ? body.selected_items : payload.selected_items,
        cust_codes: activeCustTokens,
        whse_codes: activeWhsTokens,
      });
    } catch (err) {
      setInventorySnapshotNotice(null);
      setError(err.message || "Unable to compile planner.");
    } finally {
      setCompiling(false);
    }
  };

  const handleExport = async () => {
    setDropdownOpen(false);
    setRowFilterOpen(false);
    if (compiling) return;
    if (!compiled || !lastCompiledPayload) {
      setError("Compile first before exporting Excel.");
      return;
    }

    setExporting(true);
    setError("");
    try {
      const headers = { "Content-Type": "application/json" };
      if (me?.email) headers["X-User-Email"] = me.email;
      if (me?.role) headers["X-User-Role"] = me.role || "";

      const response = await fetch(`${API_BASE}/api/mrp/operational-planner/export`, {
        method: "POST",
        headers,
        body: JSON.stringify(lastCompiledPayload),
      });
      if (!response.ok) {
        let message = "Unable to export MRP AIO.";
        try {
          const body = await response.json();
          if (body?.error === "no_data") message = "No compiled planner data available for export.";
          else if (body?.error) message = body.error;
        } catch (err) {
          // ignore parse errors
        }
        throw new Error(message);
      }

      const blob = await response.blob();
      const disposition = response.headers.get("Content-Disposition") || "";
      const suggested = filenameFromDisposition(disposition);
      const fallback = `MRP_AIO_${formatTimestampForFilename(new Date())}.xlsx`;
      const filename = suggested || fallback;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1500);
    } catch (err) {
      setError(err.message || "Unable to export MRP AIO.");
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    if (!compiled || !weeks.length || !tables.length) return undefined;
    const timer = window.setTimeout(() => {
      tables.forEach((table) => {
        const tableKey = table?.table_key || table?.label || "";
        if (!tableKey) return;
        const scrollContainer = tableScrollRefs.current[tableKey];
        const currentHeader = currentWeekHeaderRefs.current[tableKey];
        if (!scrollContainer || !currentHeader) return;
        const weekColumnWidth = currentHeader.offsetWidth || 96;
        const targetLeft = Math.max((currentHeader.offsetLeft || 0) - weekColumnWidth * 4, 0);
        scrollContainer.scrollLeft = targetLeft;
      });
    }, 0);
    return () => window.clearTimeout(timer);
  }, [compiled, weeks, tables, currentWeekStart]);

  const openDrilldown = ({ rowKey, week, walmartItemNo, value, whseCodes }) => {
    const config = SERIES_DRILLDOWN_CONFIG[rowKey];
    if (!config || !week?.week_start || !week?.week_end) return;
    if (toNumber(value) === 0) return;
    const isInTransitDrill = rowKey === "intransit_depart_pieces" || rowKey === "intransit_arrival_pieces";
    const normalizedWhseCodes = normalizeAccessTokens(whseCodes);
    const url = new URL(config.path, window.location.origin);
    url.searchParams.set("date_field", config.dateField);
    url.searchParams.set("date_from", week.week_start);
    url.searchParams.set("date_to", week.week_end);
    if (config.poBaseFilterToken) {
      url.searchParams.set("po_base_filter", config.poBaseFilterToken);
    }
    if (walmartItemNo) {
      url.searchParams.set("walmart_item_no", walmartItemNo);
    }
    if (isInTransitDrill) {
      url.searchParams.set("drill", config.drill || "");
      if (normalizedWhseCodes.length === 1) {
        url.searchParams.set("whse_code", normalizedWhseCodes[0]);
      } else {
        url.searchParams.set("whse_code", WAREHOUSE_ALL_KEY);
        if (normalizedWhseCodes.length > 1) {
          url.searchParams.set("whse_codes", normalizedWhseCodes.join(","));
        }
      }
      url.searchParams.set("cell_value", String(toNumber(value)));
    }
    if (rowKey === "shipped_qty") {
      url.searchParams.set("drill", "mrp_shipped");
      url.searchParams.set("cell_value", String(toNumber(value)));
      url.searchParams.set("mrp_value", String(toNumber(value)));
    }
    window.open(url.toString(), "_blank", "noopener,noreferrer");
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">MRP AIO (Material Requirements Planning)</h1>
          <p className="text-sm text-slate-600">
            Compile weekly PO and shipped allocation series by Walmart fiscal week (Saturday-Friday).
          </p>
          <p className="mt-1 text-xs text-slate-500">
            Planner window: Past 12 Weeks + Current Week + Future 51 Weeks.
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <UserAccessSummary />
          {compiled && <div className="text-xs text-slate-500">Last compile: {lastCompiledAt}</div>}
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex min-w-[180px] flex-col gap-1 text-xs font-medium text-slate-600">
            Start Date
            <input
              type="date"
              value={startDate}
              readOnly
              disabled
              className="rounded-md border border-slate-300 bg-slate-100 px-2.5 py-2 text-sm text-slate-900 outline-none"
            />
          </label>
          <label className="flex min-w-[180px] flex-col gap-1 text-xs font-medium text-slate-600">
            End Date
            <input
              type="date"
              value={endDate}
              readOnly
              disabled
              className="rounded-md border border-slate-300 bg-slate-100 px-2.5 py-2 text-sm text-slate-900 outline-none"
            />
          </label>

          <div className="relative min-w-[300px] flex-1">
            <div className="mb-1 text-xs font-medium text-slate-600">Item No</div>
            <button
              type="button"
              onClick={() => {
                setRowFilterOpen(false);
                setDropdownOpen((prev) => !prev);
              }}
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-left text-sm text-slate-900"
            >
              {dropdownSummary}
            </button>
            {dropdownOpen && (
              <div className="absolute left-0 right-0 z-40 mt-1 max-h-[360px] overflow-auto rounded-md border border-slate-300 bg-white p-2 shadow-lg">
                <label className="flex cursor-pointer items-start gap-2 rounded px-2 py-1 text-sm hover:bg-slate-50">
                  <input
                    type="checkbox"
                    checked={selectedItems.includes(ITEM_ALL_KEY)}
                    onChange={() => toggleItem(ITEM_ALL_KEY)}
                    className="mt-0.5"
                  />
                  <span>ALL</span>
                </label>
                <label className="flex cursor-pointer items-start gap-2 rounded px-2 py-1 text-sm hover:bg-slate-50">
                  <input
                    type="checkbox"
                    checked={selectedItems.includes(ITEM_ALL_IN_ONE_KEY)}
                    onChange={() => toggleItem(ITEM_ALL_IN_ONE_KEY)}
                    className="mt-0.5"
                  />
                  <span>All-in-One</span>
                </label>
                <div className="my-1 border-t border-slate-200" />
                {itemOptions.map((option) => (
                  <label key={option.sku} className="flex cursor-pointer items-start gap-2 rounded px-2 py-1 text-sm hover:bg-slate-50">
                    <input
                      type="checkbox"
                      checked={selectedSkuKeys.includes(option.sku)}
                      onChange={() => toggleItem(option.sku)}
                      className="mt-0.5"
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
                {!itemOptions.length && (
                  <div className="px-2 py-1 text-xs text-slate-500">No Item No available under your access scope.</div>
                )}
              </div>
            )}
          </div>

          <label className="flex min-w-[180px] flex-col gap-1 text-xs font-medium text-slate-600">
            Row Preset
            <select
              value={rowPreset}
              onChange={(event) => applyRowPreset(event.target.value)}
              className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            >
              {ROW_PRESET_OPTIONS.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <div className="relative min-w-[260px]">
            <div className="mb-1 flex items-center gap-2 text-xs font-medium text-slate-600">
              <span>Row Filter</span>
              {!presetAligned ? (
                <span className="inline-flex items-center rounded-md border border-slate-300 bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                  Custom
                </span>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => {
                setDropdownOpen(false);
                setRowFilterOpen((prev) => !prev);
              }}
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-left text-sm text-slate-900"
            >
              {visibleRowKeys.length === SERIES_ROWS.length ? "All rows visible" : `${visibleRowKeys.length} rows visible`}
            </button>
            {rowFilterOpen ? (
              <div className="absolute left-0 right-0 z-40 mt-1 max-h-[360px] overflow-auto rounded-md border border-slate-300 bg-white p-2 shadow-lg">
                {ROW_GROUPS.map((group) => (
                  <div key={group.group} className="mb-2 last:mb-0">
                    <div className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      {group.group}
                    </div>
                    {group.rows.map((row) => (
                      <label key={row.key} className="flex cursor-pointer items-start gap-2 rounded px-2 py-1 text-sm hover:bg-slate-50">
                        <input
                          type="checkbox"
                          checked={visibleRowKeySet.has(row.key)}
                          onChange={() => toggleRowVisibility(row.key)}
                          className="mt-0.5"
                        />
                        <span>{row.label}</span>
                      </label>
                    ))}
                  </div>
                ))}
              </div>
            ) : null}
          </div>

          <button
            type="button"
            onClick={handleCompile}
            disabled={!canCompile}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {compiling ? "Compiling..." : "Compile"}
          </button>
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting || compiling}
            className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
            title={!compiled || !lastCompiledPayload ? "Compile first to export Excel." : "Export the current compiled MRP AIO result."}
          >
            {exporting ? "Exporting..." : "Export Excel"}
          </button>
        </div>
        <div className="mt-2 text-xs text-slate-500">
          Fixed planner window: past 12 weeks + current week + future 51 weeks (Saturday-Friday aligned).
        </div>
      </div>

      {error && <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
      {!error && compiled && inventorySnapshotNotice?.warning ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {inventorySnapshotNotice.warning}
        </div>
      ) : null}

      {!compiled ? (
        <div className="flex-1 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-6 py-10 text-center text-slate-500">
          Set filters, then click <span className="font-semibold text-slate-700">Compile</span> to generate data.
        </div>
      ) : (
        <div className="flex-1 space-y-4 overflow-auto">
          {tables.map((table) => {
            const tableSeries = table?.series || {};
            const recommendationSummary =
              table?.recommendation_summary && typeof table.recommendation_summary === "object"
                ? table.recommendation_summary
                : null;
            const recommendationStatus = recommendationStatusConfig(recommendationSummary);
            const recommendationNotes = Array.isArray(recommendationSummary?.notes)
              ? recommendationSummary.notes.filter((note) => `${note || ""}`.trim())
              : [];
            const recommendationFields = [
              {
                key: "first_shortage_week",
                label: "First Shortage Week",
                value: recommendationFieldValue(recommendationSummary, "first_shortage_week"),
              },
              {
                key: "required_factory_ship_week",
                label: "Required Factory Ship Week",
                value: recommendationFieldValue(recommendationSummary, "required_factory_ship_week"),
              },
              {
                key: "required_qty",
                label: "Required Qty",
                value: recommendationFieldValue(recommendationSummary, "required_qty"),
              },
              {
                key: "suggested_container_plan",
                label: "Suggested Container Plan",
                value: recommendationFieldValue(recommendationSummary, "suggested_container_plan"),
              },
            ];
            return (
                <div key={table.table_key || table.label} className="rounded-lg border border-slate-300 shadow-md">
                <div className="border-b border-slate-200 bg-slate-100 px-4 py-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-sm font-semibold text-slate-800">{table.label || "Table"}</h3>
                    <span className="inline-flex items-center rounded-md border border-slate-300 bg-white px-2 py-0.5 text-xs font-medium text-slate-700">
                      WHSE Scope: <span className="ml-1 font-semibold text-slate-900">{compiledWarehouse.label || "ALL"}</span>
                    </span>
                  </div>
                </div>
                <div className="border-b border-slate-200 bg-slate-50 px-4 py-2">
                  <div className="rounded-md border border-slate-200 bg-white/70 px-3 py-2">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          Planning Recommendation
                        </div>
                        {recommendationNotes.length ? (
                          <span
                            title={recommendationNotes.join(" ")}
                            className="inline-flex cursor-help items-center rounded-md border border-slate-300 bg-white px-1.5 py-0.5 text-[11px] font-medium text-slate-500"
                          >
                            Note
                          </span>
                        ) : null}
                      </div>

                      <span
                        className={`inline-flex shrink-0 items-center rounded-md border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${recommendationStatus.badgeClass}`}
                      >
                        {recommendationStatus.label}
                      </span>
                    </div>

                    <div className="mt-2 flex flex-wrap gap-2">
                      {recommendationFields.map((field) => (
                        <div
                          key={`${table.table_key}-${field.key}`}
                          className="flex min-h-[50px] min-w-[220px] flex-1 basis-[220px] items-center justify-between gap-3 rounded-md border border-slate-200 bg-white px-2.5 py-1.5"
                        >
                          <div className="max-w-[110px] text-[10px] font-semibold uppercase tracking-wide leading-tight text-slate-500">
                            {field.label}
                          </div>
                          <div className="text-right text-base font-semibold leading-tight text-slate-900">
                            {field.value}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div
                  className="overflow-auto"
                  ref={(node) => {
                    const tableKey = table.table_key || table.label || "";
                    if (!tableKey) return;
                    if (node) tableScrollRefs.current[tableKey] = node;
                    else delete tableScrollRefs.current[tableKey];
                  }}
                >
                  <table className="min-w-[1280px] w-full border-collapse text-sm">
                    <thead className="sticky top-0 z-20 bg-slate-100">
                      <tr className="border-b border-slate-300">
                        <th className="sticky left-0 z-30 min-w-[300px] bg-slate-900 px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-100">
                          Series
                        </th>
                        {weeks.map((week) => {
                          const isCurrentWeek = week?.week_start === currentWeekStart;
                          const weekNo = String(week?.week_of_year || "").padStart(2, "0");
                          return (
                            <th
                              key={`${table.table_key}-${week.week_start}`}
                              ref={(node) => {
                                const tableKey = table.table_key || table.label || "";
                                if (!tableKey || !isCurrentWeek) return;
                                if (node) currentWeekHeaderRefs.current[tableKey] = node;
                                else delete currentWeekHeaderRefs.current[tableKey];
                              }}
                              className={`min-w-[96px] border-l px-2 py-2 text-center align-bottom ${
                                isCurrentWeek
                                  ? "border-amber-300 bg-amber-100"
                                  : "border-slate-200 bg-slate-100"
                              }`}
                              title={week.week_start}
                            >
                              <div className="text-lg font-semibold leading-tight text-slate-900">{`W${weekNo}`}</div>
                              <div className="text-[11px] text-slate-600">{week.span_label}</div>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {visibleSeriesRows.length ? visibleSeriesRows.map((row, rowIndex) => {
                        const values = Array.isArray(tableSeries?.[row.key]) ? tableSeries[row.key] : [];
                        const band = rowIndex % 2 === 0 ? "bg-white" : "bg-slate-50";
                        return (
                          <tr key={`${table.table_key}-${row.key}`} className={`border-b border-slate-200 ${band}`}>
                            <td
                              className="sticky left-0 z-10 border-r border-slate-200 bg-inherit px-4 py-2 font-medium text-slate-800"
                              title={row.tooltip || ""}
                            >
                              {row.label}
                            </td>
                            {weeks.map((week, index) => {
                              const isCurrentWeek = week?.week_start === currentWeekStart;
                              const rawValue = values[index];
                              const numericValue = toNumber(rawValue);
                              const isBlankValue = rawValue === null || rawValue === undefined || rawValue === "";
                              const isNegativeAlert = row.highlightNegative && !isBlankValue && numericValue < 0;
                              const canDrilldown = numericValue !== 0 && Boolean(SERIES_DRILLDOWN_CONFIG[row.key]);
                              return (
                                <td
                                  key={`${table.table_key}-${row.key}-${week.week_start}`}
                                  className={`px-2 py-2 text-right font-mono text-base ${
                                    isNegativeAlert
                                      ? `font-semibold text-red-800 ${isCurrentWeek ? "bg-red-100 ring-1 ring-inset ring-amber-300" : "bg-red-50"}`
                                      : `text-slate-700 ${isCurrentWeek ? "bg-amber-50" : ""}`
                                  }`}
                                >
                                  {canDrilldown ? (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        openDrilldown({
                                          rowKey: row.key,
                                          week,
                                          walmartItemNo: table?.walmart_item_no || "",
                                          value: rawValue,
                                          whseCodes: compiledWarehouse.whseCodes,
                                        })
                                      }
                                      className="w-full cursor-pointer text-right underline-offset-2 hover:underline"
                                      title="Open source records in a new tab"
                                    >
                                      {formatValue(rawValue, row.kind)}
                                    </button>
                                  ) : (
                                    isBlankValue ? "" : formatValue(rawValue, row.kind)
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      }) : (
                        <tr>
                          <td colSpan={weeks.length + 1} className="px-4 py-6 text-center text-sm text-slate-500">
                            No planner rows selected. Use Row Filter to show rows.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
